import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { fetchHoldingsData } from '../api';
import { buildApiUrl } from '../../../services/apiConfig';

const YahooWidgetsCarousel = () => {
  const scrollRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  
  // Calculate total slides based on news items (3 per slide)
  const totalSlides = Math.ceil(newsItems.length / 3) || 1;

  // Fetch holdings first
  useEffect(() => {
    const fetchUserHoldings = async () => {
      try {
        const userId = sessionStorage.getItem('user_id') || 'default_user';
        const holdingsData = await fetchHoldingsData(userId);
        setHoldings(holdingsData);
      } catch (err) {
        console.error('Error fetching holdings:', err);
      }
    };
    fetchUserHoldings();
  }, []);

  // Fetch news data from API with portfolio context
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        
        // Get top 5 holdings symbols for relevant news
        const topSymbols = holdings.slice(0, 5).map(h => h.symbol || h.asset_name).filter(Boolean).join(',');
        
        const response = await axios.get(buildApiUrl('NEWS', `/?symbols=${topSymbols}`));
        setNewsItems(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news data');
        setLoading(false);
        
        // Fallback to sample data if API fails
        setNewsItems([
          {
            id: 1,
            headline: "Markets React to Federal Reserve Policy Decision",
            source: "Yahoo Finance",
            time: "2h",
            color: "blue",
            url: "https://finance.yahoo.com/"
          },
          {
            id: 2,
            headline: "Tech Stocks Rally as Earnings Beat Expectations",
            source: "Bloomberg",
            time: "3h",
            color: "green",
            url: "https://finance.yahoo.com/"
          },
          {
            id: 3,
            headline: "Oil Prices Surge Amid Supply Concerns",
            source: "Reuters",
            time: "1h",
            color: "red",
            url: "https://finance.yahoo.com/"
          },
          {
            id: 4,
            headline: "Retail Sales Data Shows Consumer Spending Remains Strong",
            source: "CNBC",
            time: "4h",
            color: "purple",
            url: "https://finance.yahoo.com/"
          },
          {
            id: 5,
            headline: "Cryptocurrency Market Faces Regulatory Scrutiny",
            source: "Yahoo Finance",
            time: "5h",
            color: "yellow",
            url: "https://finance.yahoo.com/"
          },
          {
            id: 6,
            headline: "Housing Market Shows Signs of Cooling as Mortgage Rates Rise",
            source: "Wall Street Journal",
            time: "2h",
            color: "indigo",
            url: "https://finance.yahoo.com/"
          }
        ]);
      }
    };

    fetchNews();
    
    // Refresh news every 15 minutes
    const intervalId = setInterval(fetchNews, 15 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Auto-scroll carousel every 8 seconds
  useEffect(() => {
    if (loading || newsItems.length === 0 || isPaused) return;

    const autoScrollInterval = setInterval(() => {
      setActiveSlide((prevSlide) => {
        const nextSlide = (prevSlide + 1) % totalSlides;
        scrollToSlide(nextSlide);
        return nextSlide;
      });
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(autoScrollInterval);
  }, [loading, newsItems.length, totalSlides, isPaused]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const containerWidth = current.clientWidth;
      const scrollAmount = direction === 'left' ? -containerWidth : containerWidth;
      
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      
      // Update active slide
      setTimeout(() => {
        const newSlide = Math.round(current.scrollLeft / containerWidth);
        setActiveSlide(Math.max(0, Math.min(newSlide, totalSlides - 1)));
      }, 300);
    }
  };

  const scrollToSlide = (index) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const containerWidth = current.clientWidth;
      current.scrollTo({ left: containerWidth * index, behavior: 'smooth' });
      setActiveSlide(index);
    }
  };

  return (
    <div 
      className="mb-6"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-medium text-white">News</h2>
        <div className="flex space-x-2">
          <button 
            onClick={() => scroll('left')}
            className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
            aria-label="Scroll left"
            disabled={loading || activeSlide === 0}
          >
            <ChevronLeft size={14} className="text-gray-300" />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
            aria-label="Scroll right"
            disabled={loading || activeSlide === totalSlides - 1}
          >
            <ChevronRight size={14} className="text-gray-300" />
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-gray-800 rounded-lg h-28 p-3">
              <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-700 rounded w-1/2"></div>
              <div className="flex justify-between mt-4">
                <div className="h-3 bg-gray-700 rounded w-1/4"></div>
                <div className="h-3 bg-gray-700 rounded w-1/6"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-900/30 text-red-300 p-4 rounded-lg text-center">
          {error}
        </div>
      ) : (
        <>
          <div 
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {Array.from({ length: Math.ceil(newsItems.length / 3) }).map((_, pageIndex) => (
              <div 
                key={pageIndex}
                className="flex-shrink-0 w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 snap-start"
              >
                {newsItems.slice(pageIndex * 3, pageIndex * 3 + 3).map((news) => (
                  <YahooNewsCard key={news.id} news={news} />
                ))}
              </div>
            ))}
          </div>
          
          {/* Carousel Dots */}
          <div className="flex justify-center mt-3">
            {[...Array(totalSlides)].map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSlide(index)}
                className={`h-1.5 mx-1 rounded-full transition-all ${
                  activeSlide === index 
                    ? 'bg-primary w-6' 
                    : 'bg-gray-600 w-1.5'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Yahoo News Card Component
const YahooNewsCard = ({ news }) => {
  // Enhanced background patterns with mesh gradients
  const getBgPattern = () => {
    // Use subtle, professional colors for all news cards
    switch (news.color) {
      case 'blue': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.08),transparent_60%)] before:opacity-70`;
      case 'purple': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.08),transparent_60%)] before:opacity-70`;
      case 'green': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_20%_70%,rgba(16,185,129,0.08),transparent_60%)] before:opacity-70`;
      case 'teal': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_80%_20%,rgba(20,184,166,0.08),transparent_60%)] before:opacity-70`;
      case 'cyan': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_40%_60%,rgba(6,182,212,0.08),transparent_60%)] before:opacity-70`;
      case 'indigo': 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_60%_40%,rgba(99,102,241,0.08),transparent_60%)] before:opacity-70`;
      default: 
        return `from-slate-800/40 via-slate-700/25 to-slate-800/20
                before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_50%_50%,rgba(100,116,139,0.06),transparent_60%)] before:opacity-70`;
    }
  };

  const getAccentColor = () => {
    switch (news.color) {
      case 'blue': return 'bg-blue-500/60';
      case 'purple': return 'bg-purple-500/60';
      case 'green': return 'bg-emerald-500/60';
      case 'teal': return 'bg-teal-500/60';
      case 'cyan': return 'bg-cyan-500/60';
      case 'indigo': return 'bg-indigo-500/60';
      default: return 'bg-slate-500/60';
    }
  };

  const getSourceBg = () => {
    switch (news.color) {
      case 'blue': return 'bg-blue-950/60 text-primaryLight border border-blue-800/30';
      case 'purple': return 'bg-purple-950/60 text-purple-200 border border-purple-800/30';
      case 'green': return 'bg-emerald-950/60 text-emerald-200 border border-emerald-800/30';
      case 'teal': return 'bg-teal-950/60 text-teal-200 border border-teal-800/30';
      case 'cyan': return 'bg-cyan-950/60 text-cyan-200 border border-cyan-800/30';
      case 'indigo': return 'bg-indigo-950/60 text-indigo-200 border border-indigo-800/30';
      default: return 'bg-slate-900/60 text-slate-300 border border-slate-700/30';
    }
  };

  // Handle card click to open the news article
  const handleCardClick = () => {
    if (news.url) {
      window.open(news.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-lg shadow-sm border-gray-700/50 p-3 h-28 flex flex-col justify-between bg-gradient-to-br ${getBgPattern()} cursor-pointer transition-transform hover:scale-[1.02]`}
      onClick={handleCardClick}
    >
      {/* Accent line at top */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${getAccentColor()}`}></div>
      
      <h3 className="text-xs font-medium text-white line-clamp-3 relative z-10">
        {news.headline}
      </h3>
      
      <div className="mt-1 flex items-center justify-between text-xs relative z-10">
        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${getSourceBg()}`}>
          {news.source}
        </span>
        <div className="flex items-center">
          <span className="flex items-center text-gray-400 mr-2">
            <Clock size={10} className="mr-0.5" />
            {news.time}
          </span>
          {news.url && (
            <ExternalLink size={10} className="text-gray-400" />
          )}
        </div>
      </div>
    </div>
  );
};

export default YahooWidgetsCarousel;